<template>
    <div class="page-title" style="padding:20px 20px 0">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
            <h4 style="margin:0"><i class="fa-solid fa-money-bill-transfer" style="margin-right:6px"></i>Payment History</h4>
            <div v-if="!loading && groups.length > 0" style="font-size:13px;color:#606266">
                <span class="badge badge-secondary mr-2">{{ totalPayments }} Payments</span>
                <span class="badge badge-primary mr-2">{{ groups.length }} Enquiries</span>
                <strong>Grand Total: ₹{{ fmt(grandTotal) }}</strong>
                <span class="ml-3 text-muted">GST: ₹{{ fmt(grandGST) }}</span>
            </div>
        </div>

        <!-- Filters -->
        <el-card shadow="never" style="margin-bottom:16px;background:#f9fafb">
            <el-row :gutter="12" style="align-items:flex-end">
                <el-col :span="5">
                    <div style="font-size:12px;color:#606266;margin-bottom:4px">Date Range (Received On)</div>
                    <el-date-picker
                        v-model="form.dates"
                        type="daterange"
                        range-separator="→"
                        start-placeholder="From"
                        end-placeholder="To"
                        format="DD/MM/YYYY"
                        value-format="YYYY-MM-DD"
                        size="small"
                        style="width:100%"
                    />
                </el-col>
                <el-col :span="3">
                    <div style="font-size:12px;color:#606266;margin-bottom:4px">Enquiry ID</div>
                    <el-input
                        v-model="form.enquiry_id"
                        placeholder="e.g. 1234"
                        size="small"
                        clearable
                        @keyup.enter="getlist()"
                    />
                </el-col>
                <el-col :span="3">
                    <div style="font-size:12px;color:#606266;margin-bottom:4px">Payment Status</div>
                    <el-select v-model="form.payment_status" size="small" clearable placeholder="All" style="width:100%">
                        <el-option label="Pending" value="Pending" />
                        <el-option label="Inprogress" value="Inprogress" />
                        <el-option label="Received" value="Received" />
                    </el-select>
                </el-col>
                <el-col :span="3">
                    <div style="font-size:12px;color:#606266;margin-bottom:4px">Payment Source</div>
                    <el-select v-model="form.payment_source" size="small" clearable placeholder="All" style="width:100%">
                        <el-option label="Online" value="Online" />
                        <el-option label="Cash" value="Cash" />
                        <el-option label="Cheque" value="Cheque" />
                    </el-select>
                </el-col>
                <el-col :span="3">
                    <div style="font-size:12px;color:#606266;margin-bottom:4px">GST Filed Status</div>
                    <el-select v-model="form.is_filed" size="small" placeholder="All" style="width:100%">
                        <el-option label="All" value="" />
                        <el-option label="Filed" value="1" />
                        <el-option label="Not Filed" value="0" />
                    </el-select>
                </el-col>
                <el-col :span="3">
                    <div style="font-size:12px;color:#606266;margin-bottom:4px">Payment Type</div>
                    <el-select v-model="form.is_flight" size="small" placeholder="All" style="width:100%">
                        <el-option label="All" value="" />
                        <el-option label="Package" value="0" />
                        <el-option label="Flight" value="1" />
                    </el-select>
                </el-col>
                <el-col :span="4" style="display:flex;gap:8px;align-items:flex-end">
                    <el-button type="primary" size="small" @click="getlist()" :loading="loading" round>
                        <i class="fa-solid fa-filter" style="margin-right:4px"></i> Apply
                    </el-button>
                    <el-button size="small" @click="resetFilters()" round>
                        Reset
                    </el-button>
                </el-col>
            </el-row>
        </el-card>
    </div>

    <!-- Loading -->
    <div v-if="loading" style="text-align:center;padding:40px">
        <i class="fa-solid fa-spinner fa-spin fa-2x"></i>
        <p style="margin-top:12px;color:#909399">Loading payment history...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && groups.length === 0" style="text-align:center;padding:60px 20px">
        <i class="fa-solid fa-receipt fa-3x" style="color:#c0c4cc;margin-bottom:16px;display:block"></i>
        <p style="color:#909399;font-size:15px">No payments found matching your filters.</p>
    </div>

    <!-- Enquiry Groups -->
    <div v-else style="padding:0 20px 20px">
        <el-card
            v-for="group in groups"
            :key="group.enquiry_id"
            class="mb-3"
            shadow="hover"
        >
            <!-- Card Header -->
            <template #header>
                <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px">
                    <div style="display:flex;align-items:center;gap:12px">
                        <el-tag type="primary" effect="dark" size="small">
                            Enquiry #{{ group.enquiry_id }}
                        </el-tag>
                        <span style="font-weight:600;font-size:14px">{{ group.customer_name }}</span>
                        <span v-if="group.mobile" style="color:#606266;font-size:13px">
                            <i class="fa-solid fa-phone" style="margin-right:3px"></i>{{ group.mobile }}
                        </span>
                        <el-tag v-if="group.destinations" type="info" size="small" style="max-width:200px;overflow:hidden;text-overflow:ellipsis">
                            {{ group.destinations }}
                        </el-tag>
                    </div>
                    <div style="display:flex;gap:16px;font-size:13px">
                        <span>
                            <span style="color:#909399">Total:</span>
                            <strong style="margin-left:4px;color:#303133">₹{{ fmt(group.total_amount) }}</strong>
                        </span>
                        <span>
                            <span style="color:#909399">GST:</span>
                            <strong style="margin-left:4px;color:#e6a23c">₹{{ fmt(group.total_gst) }}</strong>
                        </span>
                        <span v-if="group.total_tcs > 0">
                            <span style="color:#909399">TCS:</span>
                            <strong style="margin-left:4px;color:#f56c6c">₹{{ fmt(group.total_tcs) }}</strong>
                        </span>
                        <span>
                            <el-badge
                                :value="group.filed_count + '/' + group.payments.length"
                                :type="group.filed_count === group.payments.length ? 'success' : 'warning'"
                                style="margin-top:2px"
                            >
                                <el-tag size="small" :type="group.filed_count === group.payments.length ? 'success' : 'warning'">
                                    Filed
                                </el-tag>
                            </el-badge>
                        </span>
                    </div>
                </div>
            </template>

            <!-- Payments Table -->
            <el-table
                :data="group.payments"
                size="small"
                border
                style="width:100%"
                :row-class-name="rowClass"
            >
                <el-table-column label="#" width="40" align="center">
                    <template #default="scope">{{ scope.$index + 1 }}</template>
                </el-table-column>

                <el-table-column label="Date" width="110">
                    <template #default="scope">{{ scope.row.received_on }}</template>
                </el-table-column>

                <el-table-column label="Type" width="90" align="center">
                    <template #default="scope">
                        <el-tag size="small" :type="parseInt(scope.row.is_flight) === 1 ? 'warning' : 'success'" effect="plain">
                            {{ parseInt(scope.row.is_flight) === 1 ? 'Flight' : 'Package' }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="Amount" width="110" align="right">
                    <template #default="scope">
                        <strong>₹{{ fmt(scope.row.amount) }}</strong>
                    </template>
                </el-table-column>

                <el-table-column label="GST" width="110" align="right">
                    <template #default="scope">
                        <span style="color:#e6a23c">₹{{ fmt(scope.row.amount_gst) }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="Base (ex-GST)" width="120" align="right">
                    <template #default="scope">
                        ₹{{ fmt(scope.row.amount_without_gst) }}
                    </template>
                </el-table-column>

                <el-table-column label="TCS" width="95" align="right">
                    <template #default="scope">
                        <span v-if="parseFloat(scope.row.tcs) > 0" style="color:#f56c6c">
                            ₹{{ fmt(scope.row.tcs) }}
                        </span>
                        <span v-else style="color:#c0c4cc">—</span>
                    </template>
                </el-table-column>

                <el-table-column prop="payment_source" label="Source" width="90" />

                <el-table-column prop="received_by" label="Received By" width="120" />

                <el-table-column label="Status" width="110" align="center">
                    <template #default="scope">
                        <el-tag size="small" type="info"    v-if="scope.row.payment_status === 'Pending'">Pending</el-tag>
                        <el-tag size="small" type="warning" v-else-if="scope.row.payment_status === 'Inprogress'">Inprogress</el-tag>
                        <el-tag size="small" type="success" v-else-if="scope.row.payment_status === 'Received'">Received</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="GST Filed" width="95" align="center">
                    <template #default="scope">
                        <el-checkbox
                            :model-value="parseInt(scope.row.is_filed) === 1"
                            :true-value="true"
                            :false-value="false"
                            :loading="scope.row._toggling"
                            @change="(val) => toggleFiled(scope.row, val)"
                        />
                    </template>
                </el-table-column>

                <el-table-column label="Attachment" width="100" align="center">
                    <template #default="scope">
                        <el-link
                            v-if="scope.row.attachment"
                            type="primary"
                            size="small"
                            @click="viewfile(scope.row.attachment.link)"
                            :underline="false"
                        >
                            <i class="fa-solid fa-paperclip"></i> View
                        </el-link>
                        <span v-else style="color:#c0c4cc;font-size:12px">—</span>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>
    </div>
</template>

<script>
import {
    ElCard, ElRow, ElCol, ElForm, ElFormItem, ElDatePicker, ElInput,
    ElSelect, ElOption, ElButton, ElTable, ElTableColumn, ElTag,
    ElCheckbox, ElLink, ElBadge
} from 'element-plus'

export default {
    name: 'PaymentHistoryList',
    components: {
        ElCard, ElRow, ElCol, ElForm, ElFormItem, ElDatePicker, ElInput,
        ElSelect, ElOption, ElButton, ElTable, ElTableColumn, ElTag,
        ElCheckbox, ElLink, ElBadge
    },
    data() {
        return {
            loading: false,
            groups: [],
            form: {
                dates: null,
                enquiry_id: '',
                payment_status: '',
                payment_source: '',
                is_filed: '',
                is_flight: '',
            },
        }
    },
    computed: {
        totalPayments() {
            return this.groups.reduce((sum, g) => sum + g.payments.length, 0)
        },
        grandTotal() {
            return this.groups.reduce((sum, g) => sum + g.total_amount, 0)
        },
        grandGST() {
            return this.groups.reduce((sum, g) => sum + g.total_gst, 0)
        },
        grandTCS() {
            return this.groups.reduce((sum, g) => sum + g.total_tcs, 0)
        },
    },
    mounted() {
        this.getlist()
    },
    methods: {
        fmt(val) {
            return Intl.NumberFormat('en-IN').format(
                Math.round((parseFloat(val) || 0) * 100) / 100
            )
        },
        rowClass({ row }) {
            if (parseInt(row.is_filed) === 1) return 'filed-row'
            return ''
        },
        resetFilters() {
            this.form.dates         = null
            this.form.enquiry_id    = ''
            this.form.payment_status = ''
            this.form.payment_source = ''
            this.form.is_filed      = ''
            this.form.is_flight     = ''
            this.getlist()
        },
        getlist() {
            this.loading = true
            const payload = {
                enquiry_id:     this.form.enquiry_id     || '',
                payment_status: this.form.payment_status || '',
                payment_source: this.form.payment_source || '',
                is_filed:       this.form.is_filed,
                is_flight:      this.form.is_flight,
                date_from: this.form.dates ? this.form.dates[0] : '',
                date_to:   this.form.dates ? this.form.dates[1] : '',
            }
            axios.post('/payment-history/list', payload)
                .then(res => {
                    this.groups = res.data.map(g => ({
                        ...g,
                        filed_count: g.payments.filter(p => parseInt(p.is_filed) === 1).length,
                        payments: g.payments.map(p => ({ ...p, _toggling: false })),
                    }))
                    this.loading = false
                })
                .catch(() => {
                    toastr.error('Failed to load payment history.')
                    this.loading = false
                })
        },
        toggleFiled(payment, val) {
            payment._toggling = true
            const newVal = val ? 1 : 0
            axios.post('/payment/togglefiled', { id: payment.id, is_filed: newVal })
                .then(() => {
                    payment.is_filed = newVal
                    payment._toggling = false
                    // update filed_count on parent group
                    this.groups.forEach(g => {
                        const found = g.payments.find(p => p.id === payment.id)
                        if (found) {
                            g.filed_count = g.payments.filter(p => parseInt(p.is_filed) === 1).length
                        }
                    })
                    toastr.success(newVal ? 'Marked as Filed.' : 'Marked as Not Filed.')
                })
                .catch(() => {
                    payment._toggling = false
                    toastr.error('Failed to update filed status.')
                })
        },
        viewfile(link) {
            window.open(link, '_blank', 'width=' + screen.availWidth + ',height=' + screen.availHeight)
        },
    }
}
</script>

<style scoped>
:deep(.filed-row td) {
    background-color: #f0faf0 !important;
}
:deep(.el-table .filed-row) {
    --el-table-tr-bg-color: #f0faf0;
}
</style>
